import { DatasetCard } from "@/features/dataset";
import type { Dataset } from "@/types/europafacts";
import { Stack, Tabs, Text, Title } from "@mantine/core";
import { IconClock, IconTrendingUp } from "@tabler/icons-react";
import { useState } from "react";

// Mock datasets - in production, these would be fetched from the API
const mockRecentlyUploaded: Dataset[] = [
  {
    bound_box_json: { x1: 0, x2: 0, y1: 0, y2: 0 },
    byte_count: 5242880,
    column_count: 12,
    country_id: "dk",
    data_file_original: "",
    data_file_processed: null,
    data_source_id: null,
    datetime_column_rename: new Date().toISOString(),
    datetime_created: new Date(
      Date.now() - 2 * 24 * 60 * 60 * 1000
    ).toISOString(),
    datetime_data_source_issued: new Date().toISOString(),
    datetime_data_source_updated: new Date().toISOString(),
    datetime_parquet_meta: null,
    datetime_publish: null,
    datetime_semantic: null,
    datetime_statistic: new Date().toISOString(),
    datetime_string_to_number: new Date().toISOString(),
    datetime_translation: null,
    datetime_updated: new Date().toISOString(),
    db_table_id: "recent-1",
    db_table_type: "",
    description_english:
      "European climate data showing temperature trends across major cities. Updated monthly with comprehensive meteorological measurements.",
    description_long_english:
      "European climate data showing temperature trends across major cities. Updated monthly with comprehensive meteorological measurements.",
    description_long_original:
      "European climate data showing temperature trends across major cities. Updated monthly with comprehensive meteorological measurements.",
    description_long_translation: {},
    description_original:
      "European climate data showing temperature trends across major cities. Updated monthly with comprehensive meteorological measurements.",
    description_short_english:
      "European climate data showing temperature trends across major cities.",
    description_short_original:
      "European climate data showing temperature trends across major cities.",
    description_short_translation: {},
    description_translation: {},
    duplicate_table_id: null,
    hash_id: "",
    icon: null,
    language_id: "en",
    language_iso: "en",
    model_name: "",
    quality_measurement: null,
    row_count: 15420,
    source_json_original: {
      table: {},
      column_list: [],
      data_source: {
        data_realm_name: "eurostat.eu",
      },
    },
    table_mismatch: false,
    table_name: "European Climate Trends 2024",
    table_name_english: "European Climate Trends 2024",
    table_name_original: "European Climate Trends 2024",
    table_name_sql: "",
    table_name_suggestion: "European Climate Trends 2024",
    table_name_translation: {},
    table_normal_data: true,
    table_usage_english: "",
    table_usage_original: null,
    table_usage_translation: {},
    user_owner_id: null,
  },
  {
    bound_box_json: { x1: 0, x2: 0, y1: 0, y2: 0 },
    byte_count: 3145728,
    column_count: 8,
    country_id: "de",
    data_file_original: "",
    data_file_processed: null,
    data_source_id: null,
    datetime_column_rename: new Date().toISOString(),
    datetime_created: new Date(
      Date.now() - 5 * 24 * 60 * 60 * 1000
    ).toISOString(),
    datetime_data_source_issued: new Date().toISOString(),
    datetime_data_source_updated: new Date().toISOString(),
    datetime_parquet_meta: null,
    datetime_publish: null,
    datetime_semantic: null,
    datetime_statistic: new Date().toISOString(),
    datetime_string_to_number: new Date().toISOString(),
    datetime_translation: null,
    datetime_updated: new Date().toISOString(),
    db_table_id: "recent-2",
    db_table_type: "",
    description_english:
      "German employment statistics by sector and region. Quarterly updates with detailed breakdowns of employment trends.",
    description_long_english:
      "German employment statistics by sector and region. Quarterly updates with detailed breakdowns of employment trends.",
    description_long_original:
      "German employment statistics by sector and region. Quarterly updates with detailed breakdowns of employment trends.",
    description_long_translation: {},
    description_original:
      "German employment statistics by sector and region. Quarterly updates with detailed breakdowns of employment trends.",
    description_short_english:
      "German employment statistics by sector and region.",
    description_short_original:
      "German employment statistics by sector and region.",
    description_short_translation: {},
    description_translation: {},
    duplicate_table_id: null,
    hash_id: "",
    icon: null,
    language_id: "de",
    language_iso: "de",
    model_name: "",
    quality_measurement: null,
    row_count: 8920,
    source_json_original: {
      table: {},
      column_list: [],
      data_source: {
        data_realm_name: "destatis.de",
      },
    },
    table_mismatch: false,
    table_name: "German Employment Statistics Q4 2024",
    table_name_english: "German Employment Statistics Q4 2024",
    table_name_original: "German Employment Statistics Q4 2024",
    table_name_sql: "",
    table_name_suggestion: "German Employment Statistics Q4 2024",
    table_name_translation: {},
    table_normal_data: true,
    table_usage_english: "",
    table_usage_original: null,
    table_usage_translation: {},
    user_owner_id: null,
  },
];

const mockMostUsed: Dataset[] = [
  {
    bound_box_json: { x1: 0, x2: 0, y1: 0, y2: 0 },
    byte_count: 10485760,
    column_count: 15,
    country_id: "eu",
    data_file_original: "",
    data_file_processed: null,
    data_source_id: null,
    datetime_column_rename: new Date().toISOString(),
    datetime_created: new Date(
      Date.now() - 30 * 24 * 60 * 60 * 1000
    ).toISOString(),
    datetime_data_source_issued: new Date().toISOString(),
    datetime_data_source_updated: new Date().toISOString(),
    datetime_parquet_meta: null,
    datetime_publish: null,
    datetime_semantic: null,
    datetime_statistic: new Date().toISOString(),
    datetime_string_to_number: new Date().toISOString(),
    datetime_translation: null,
    datetime_updated: new Date().toISOString(),
    db_table_id: "popular-1",
    db_table_type: "",
    description_english:
      "EU GDP and economic indicators dataset. Comprehensive quarterly data covering all member states with detailed economic metrics.",
    description_long_english:
      "EU GDP and economic indicators dataset. Comprehensive quarterly data covering all member states with detailed economic metrics.",
    description_long_original:
      "EU GDP and economic indicators dataset. Comprehensive quarterly data covering all member states with detailed economic metrics.",
    description_long_translation: {},
    description_original:
      "EU GDP and economic indicators dataset. Comprehensive quarterly data covering all member states with detailed economic metrics.",
    description_short_english: "EU GDP and economic indicators dataset.",
    description_short_original: "EU GDP and economic indicators dataset.",
    description_short_translation: {},
    description_translation: {},
    duplicate_table_id: null,
    hash_id: "",
    icon: null,
    language_id: "en",
    language_iso: "en",
    model_name: "",
    quality_measurement: null,
    row_count: 45600,
    source_json_original: {
      table: {},
      column_list: [],
      data_source: {
        data_realm_name: "eurostat.eu",
      },
    },
    table_mismatch: false,
    table_name: "EU GDP and Economic Indicators",
    table_name_english: "EU GDP and Economic Indicators",
    table_name_original: "EU GDP and Economic Indicators",
    table_name_sql: "",
    table_name_suggestion: "EU GDP and Economic Indicators",
    table_name_translation: {},
    table_normal_data: true,
    table_usage_english: "",
    table_usage_original: null,
    table_usage_translation: {},
    user_owner_id: null,
  },
  {
    bound_box_json: { x1: 0, x2: 0, y1: 0, y2: 0 },
    byte_count: 8388608,
    column_count: 10,
    country_id: "fr",
    data_file_original: "",
    data_file_processed: null,
    data_source_id: null,
    datetime_column_rename: new Date().toISOString(),
    datetime_created: new Date(
      Date.now() - 60 * 24 * 60 * 60 * 1000
    ).toISOString(),
    datetime_data_source_issued: new Date().toISOString(),
    datetime_data_source_updated: new Date().toISOString(),
    datetime_parquet_meta: null,
    datetime_publish: null,
    datetime_semantic: null,
    datetime_statistic: new Date().toISOString(),
    datetime_string_to_number: new Date().toISOString(),
    datetime_translation: null,
    datetime_updated: new Date().toISOString(),
    db_table_id: "popular-2",
    db_table_type: "",
    description_english:
      "French population demographics and migration data. Annual census data with detailed age, gender, and geographic distributions.",
    description_long_english:
      "French population demographics and migration data. Annual census data with detailed age, gender, and geographic distributions.",
    description_long_original:
      "French population demographics and migration data. Annual census data with detailed age, gender, and geographic distributions.",
    description_long_translation: {},
    description_original:
      "French population demographics and migration data. Annual census data with detailed age, gender, and geographic distributions.",
    description_short_english:
      "French population demographics and migration data.",
    description_short_original:
      "French population demographics and migration data.",
    description_short_translation: {},
    description_translation: {},
    duplicate_table_id: null,
    hash_id: "",
    icon: null,
    language_id: "fr",
    language_iso: "fr",
    model_name: "",
    quality_measurement: null,
    row_count: 32100,
    source_json_original: {
      table: {},
      column_list: [],
      data_source: {
        data_realm_name: "insee.fr",
      },
    },
    table_mismatch: false,
    table_name: "French Demographics 2024",
    table_name_english: "French Demographics 2024",
    table_name_original: "French Demographics 2024",
    table_name_sql: "",
    table_name_suggestion: "French Demographics 2024",
    table_name_translation: {},
    table_normal_data: true,
    table_usage_english: "",
    table_usage_original: null,
    table_usage_translation: {},
    user_owner_id: null,
  },
];

export function WhatOthersAreDoing() {
  const [activeTab, setActiveTab] = useState<string | null>("recent");

  return (
    <Stack gap="xl">
      <Title order={2} fw={600} fz={28}>
        What Others Are Doing
      </Title>
      <Text c="gray.9" fz={16} lh={1.6}>
        Discover popular datasets and see what's new in the community. Explore
        what others are working with to find inspiration for your next project.
      </Text>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="recent" leftSection={<IconClock size={16} />}>
            Recently Uploaded
          </Tabs.Tab>
          <Tabs.Tab value="popular" leftSection={<IconTrendingUp size={16} />}>
            Most Used
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="recent" pt="lg">
          <Stack gap="md">
            {mockRecentlyUploaded.map((dataset) => (
              <DatasetCard key={dataset.db_table_id} dataset={dataset} />
            ))}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="popular" pt="lg">
          <Stack gap="md">
            {mockMostUsed.map((dataset) => (
              <DatasetCard key={dataset.db_table_id} dataset={dataset} />
            ))}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
